import React, { MouseEventHandler, PropsWithChildren, RefObject } from 'react'
import classnames from 'classnames'
import { Icon } from '@atoms'

type FilterButtonProps = {
    name: string
    label: React.ReactNode
    description?: React.ReactNode
    valueLabel: React.ReactNode
    onClick?: MouseEventHandler
    disabled?: boolean
    className?: string
    buttonRef?: RefObject<HTMLButtonElement>
    wrapperClassname?: string
    iconClassname?: string
    isError?: boolean
}

export const FilterButton: React.FC<FilterButtonProps> = (props) => {
    const { onClick, disabled, name, description } = props

    const className = classnames(
        'group',
        'border border-neutral-300 rounded-lg shadow-xs py-2 px-3',
        'outline-0',
        'bg-white',
        'hover:bg-blue-50 hover:border-blue-200',
        'active:bg-blue-100 active:border-blue-200',
        'disabled:bg-neutral-50 disabled:border-neutral-200',
        props.className
    )

    const wrapperClassname = classnames(
        'flex w-full flex-row items-center justify-between',
        props.wrapperClassname
    )

    const iconClassname = classnames(
        'ml-2 text-neutral-800 group-active:text-neutral-900 group-disabled:text-disabled',
        props.iconClassname
    )

    return (
        <button
            type="button"
            className={className}
            onClick={onClick}
            disabled={disabled}
            data-testid={name}
            ref={props.buttonRef}
        >
            <div className="flex w-full flex-col items-start">
                <div className={wrapperClassname}>
                    <Label {...props} isError={props.isError} />
                    <Icon
                        className={iconClassname}
                        name="expand_more"
                        size="small"
                    />
                </div>
                {description && <Description>{description}</Description>}
            </div>
        </button>
    )
}
const Label: React.FC<
    Pick<FilterButtonProps, 'label' | 'valueLabel' | 'isError'>
> = (props) => {
    const { label, valueLabel, isError } = props

    if (!!label && !!valueLabel) {
        return (
            <div className="flex grow items-center gap-2 overflow-hidden">
                <MainLabel>{label}:</MainLabel>
                <div className="flex items-center gap-1 overflow-hidden">
                    <SecondaryLabel>{valueLabel}</SecondaryLabel>
                    {isError && (
                        <Icon
                            name="error"
                            className="text-red-500"
                            size="small"
                        />
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="grow">
            <MainLabel>{valueLabel}</MainLabel>
        </div>
    )
}

const MainLabel: React.FC<PropsWithChildren> = ({ children }) => {
    return (
        <div
            data-testid="main-label"
            className="whitespace-nowrap text-left text-regular group-disabled:text-disabled"
        >
            {children}
        </div>
    )
}

const SecondaryLabel: React.FC<PropsWithChildren> = ({ children }) => {
    return (
        <div
            data-testid="secondary-label"
            className="overflow-hidden text-ellipsis whitespace-nowrap text-azure group-hover:text-blue-600 group-active:text-blue-700 group-disabled:text-disabled"
        >
            {children}
        </div>
    )
}

const Description: React.FC<PropsWithChildren> = ({ children }) => {
    return (
        <div
            data-testid="description"
            className="text-body-small pointer-events-none mt-2 w-full text-start font-normal text-subtle"
        >
            {children}
        </div>
    )
}
